import { User } from '../types';

/**
 * Check if user has active unlimited tokens
 * Enhanced with robust date parsing and edge case handling
 */
export const hasUnlimitedTokens = (user: User | null): boolean => {
    // Quick null checks
    if (!user) return false;
    if (!user.activePromoEffects) return false;
    if (!user.activePromoEffects.unlimitedTokensUntil) return false;
    
    try {
        const now = new Date();
        const expiryString = user.activePromoEffects.unlimitedTokensUntil;
        
        // Handle various date formats
        let expiry: Date;
        if (typeof expiryString === 'number') {
            expiry = new Date(expiryString);
        } else if (typeof expiryString === 'string') {
            expiry = new Date(expiryString);
        } else {
            console.warn('[PromoEffects] Invalid unlimitedTokensUntil format:', typeof expiryString);
            return false;
        }
        
        // Check for invalid date
        if (isNaN(expiry.getTime())) {
            console.warn('[PromoEffects] Invalid date in unlimitedTokensUntil:', expiryString);
            return false;
        }
        
        return expiry > now;
    } catch (error) {
        console.error('[PromoEffects] Error checking unlimited tokens:', error);
        return false;
    }
};

/**
 * Get active token multiplier
 */
export const getTokenMultiplier = (user: User | null): number => {
    if (!user?.activePromoEffects?.tokenMultiplier || !user.activePromoEffects.tokenMultiplierUntil) {
        return 1;
    }
    
    const now = new Date();
    const expiry = new Date(user.activePromoEffects.tokenMultiplierUntil);
    
    if (expiry > now) {
        return user.activePromoEffects.tokenMultiplier;
    }
    
    return 1;
};

/**
 * Check if a specific feature is unlocked
 */
export const hasFeatureUnlocked = (user: User | null, featureId: string): boolean => {
    if (!user?.activePromoEffects?.unlockedFeatures || !user.activePromoEffects.featureUnlocksUntil) {
        return false;
    }
    
    const now = new Date();
    const expiry = new Date(user.activePromoEffects.featureUnlocksUntil);
    
    if (expiry > now) {
        return user.activePromoEffects.unlockedFeatures.includes(featureId);
    }
    
    return false;
};

/**
 * Clean up expired promo effects from user object
 * Returns updated user object if changes were made, null otherwise
 */
export const cleanupExpiredEffects = (user: User): User | null => {
    if (!user.activePromoEffects) return null;
    
    const now = new Date();
    let hasChanges = false;
    const updatedEffects = { ...user.activePromoEffects };
    
    // Check unlimited tokens
    if (updatedEffects.unlimitedTokensUntil) {
        const expiry = new Date(updatedEffects.unlimitedTokensUntil);
        if (expiry <= now) {
            delete updatedEffects.unlimitedTokensUntil;
            hasChanges = true;
        }
    }
    
    // Check token multiplier
    if (updatedEffects.tokenMultiplierUntil) {
        const expiry = new Date(updatedEffects.tokenMultiplierUntil);
        if (expiry <= now) {
            delete updatedEffects.tokenMultiplier;
            delete updatedEffects.tokenMultiplierUntil;
            hasChanges = true;
        }
    }
    
    // Check feature unlocks
    if (updatedEffects.featureUnlocksUntil) {
        const expiry = new Date(updatedEffects.featureUnlocksUntil);
        if (expiry <= now) {
            delete updatedEffects.unlockedFeatures;
            delete updatedEffects.featureUnlocksUntil;
            hasChanges = true;
        }
    }
    
    if (!hasChanges) return null;
    
    // If all effects are removed, delete the activePromoEffects object
    if (Object.keys(updatedEffects).length === 0) {
        const { activePromoEffects, ...userWithoutEffects } = user;
        return userWithoutEffects as User;
    }
    
    return {
        ...user,
        activePromoEffects: updatedEffects
    };
};

/**
 * Calculate effective token limit considering promo effects
 */
export const getEffectiveTokenLimit = (user: User | null, baseLimit: number): number => {
    if (hasUnlimitedTokens(user)) {
        return Infinity;
    }
    return baseLimit;
};

/**
 * Calculate tokens to add on refresh considering multiplier
 */
export const calculateTokenRefresh = (user: User | null, baseRefreshAmount: number): number => {
    const multiplier = getTokenMultiplier(user);
    return Math.floor(baseRefreshAmount * multiplier);
};

/**
 * Get display text for active effects
 */
export const getActiveEffectsDisplay = (user: User | null): string[] => {
    const effects: string[] = [];
    
    if (hasUnlimitedTokens(user)) {
        effects.push('🚀 Unlimited Tokens');
    }
    
    const multiplier = getTokenMultiplier(user);
    if (multiplier > 1) {
        effects.push(`⚡ ${multiplier}x Token Boost`);
    }
    
    if (user?.activePromoEffects?.unlockedFeatures && user.activePromoEffects.featureUnlocksUntil) {
        const now = new Date();
        const expiry = new Date(user.activePromoEffects.featureUnlocksUntil);
        if (expiry > now) {
            effects.push(`🔓 ${user.activePromoEffects.unlockedFeatures.length} Features Unlocked`);
        }
    }
    
    return effects;
};
